import React, { useEffect } from 'react';
import { Navbar } from '../layout/Navbar';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Shield, TrendingUp, TrendingDown, Wallet, AlertCircle, CheckCircle } from 'lucide-react';
import { usePaperTrading } from '../../contexts/PaperTradingContext';
import { useNavigate } from 'react-router-dom';
import { useRecommendations } from '../../hooks/useRecommendations';
import { MyAgents } from './MyAgents';
import { TradeHistory } from './TradeHistory';
import authService from '../../services/authService';

export const Portfolio = () => {
    const [user, setUser] = React.useState(null);

    React.useEffect(() => {
        const unsubscribe = authService.subscribeToAuthChanges((u) => {
            setUser(u);
        });
        return () => unsubscribe();
    }, []);
    const {
        balance,
        positions,
        totalEquity,
        loading,
        refreshPortfolio
    } = usePaperTrading();

    const { recommendations } = useRecommendations();
    const navigate = useNavigate();

    // Trigger price refresh on mount and every 30s
    useEffect(() => {
        refreshPortfolio();
        const interval = setInterval(refreshPortfolio, 30000);
        return () => clearInterval(interval);
    }, [refreshPortfolio]);

    // Format positions for the UI
    const holdings = positions.map((pos, index) => {
        const entryPrice = pos.avgPrice || pos.price || 0.5;
        const currentPrice = pos.currentPrice || entryPrice;
        const value = (pos.amount / entryPrice) * currentPrice; // Current Value
        const initialValue = pos.invested || pos.amount; // Invested Amount
        const percentChange = ((value - initialValue) / initialValue) * 100;

        // Use a consistent color palette
        const colors = ['#627EEA', '#14F195', '#FF9900', '#FF3B30', '#007AFF', '#5856D6', '#AF52DE', '#FF2D55'];

        // Find recommendation for this asset
        const rec = recommendations.find(r => r.marketSlug === pos.marketSlug && r.positionSide === pos.side);

        return {
            name: pos.marketTitle,
            symbol: pos.side, // Using Side as symbol for now
            amount: (pos.amount / entryPrice).toFixed(1), // Shares
            value: value,
            change: percentChange,
            color: colors[index % colors.length],
            marketSlug: pos.marketSlug,
            recommendation: rec || null
        };
    });

    const totalWorth = totalEquity;
    const data = holdings.map(h => ({ name: h.name, value: h.value }));

    if (loading) {
        return (
            <div className="min-h-screen bg-black text-white flex items-center justify-center">
                <div className="animate-pulse">Loading Portfolio...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-black text-foreground pb-20">
            <Navbar />
            <div className="container mx-auto px-4 pt-32">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4">
                    <div>
                        <h1 className="text-3xl font-black text-white">Portfolio</h1>
                        {user && (
                            <div className="flex items-center gap-4 mt-4 bg-white/5 p-4 rounded-xl border border-white/10">
                                {user.photoURL ? (
                                    <img src={user.photoURL} alt="Avatar" className="w-12 h-12 rounded-full border-2 border-primary/50" />
                                ) : (
                                    <div className="w-12 h-12 bg-primary/20 rounded-full border-2 border-primary/50 flex items-center justify-center font-bold text-primary text-lg">
                                        {(user.displayName || user.email || 'U')[0].toUpperCase()}
                                    </div>
                                )}
                                <div className="flex-1">
                                    <div className="font-bold text-white text-lg leading-tight flex items-center gap-2">
                                        {user.displayName || 'User'}
                                        <span className="px-2 py-0.5 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] font-bold rounded-full uppercase tracking-wider">
                                            Active Member
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
                                        <span>{user.email}</span>
                                        <span>•</span>
                                        <span>Joined {new Date(user.metadata?.creationTime || Date.now()).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    <button className="px-4 py-2 border border-white/10 rounded-lg hover:bg-white/5 transition-colors text-sm">Settings</button>
                </div>

                <div className="grid lg:grid-cols-3 gap-6 mb-8">
                    {/* Total Net Worth Card */}
                    <div className="lg:col-span-2 bg-[#1A1A1A] border border-white/10 rounded-2xl p-8 relative overflow-hidden group">
                        <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity">
                            <Wallet size={160} />
                        </div>
                        <h2 className="text-muted-foreground font-mono uppercase tracking-wider text-sm mb-2">Total Net Worth</h2>
                        <div className="text-5xl md:text-6xl font-mono font-bold text-white mb-6">
                            ${totalWorth.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="inline-flex items-center gap-1 px-3 py-1 bg-secondary/10 text-secondary rounded text-sm font-bold border border-secondary/20">
                                <TrendingUp size={14} /> Available Cash: ${balance.toFixed(2)}
                            </div>
                        </div>
                    </div>

                    {/* AI Risk Score Card */}
                    <div className="bg-gradient-to-br from-accent/20 to-primary/20 border border-white/5 rounded p-8 flex flex-col justify-center items-center text-center relative overflow-hidden">
                        <div className="absolute top-4 right-4 text-xs font-bold text-accent border border-accent/30 px-2 py-0.5 rounded">AI ANALYSIS</div>
                        <Shield className="w-16 h-16 text-accent mb-4 opacity-80" />
                        <h3 className="text-lg font-bold text-white mb-0 uppercase tracking-tight">Risk Score</h3>
                        <div className="text-7xl font-black text-transparent bg-clip-text bg-gradient-to-r from-accent to-secondary my-2">
                            A-
                        </div>
                        <p className="text-sm text-[#6a6a6a] italic">
                            "Portfolio reflects active prediction strategy. Monitor volatility events."
                        </p>
                    </div>
                </div>

                {/* Bottom Section - 3 Columns Layout */}
                <div className="grid lg:grid-cols-3 gap-8 mt-8">
                    {/* Trade History */}
                    <div className="lg:col-span-1">
                        <TradeHistory />
                    </div>

                    {/* Asset Allocation */}
                    <div className="lg:col-span-2 bg-[#1A1A1A] border border-white/10 rounded-2xl p-6">
                        <h3 className="text-xl font-bold text-white mb-6">Asset Allocation</h3>
                        {holdings.length > 0 ? (
                            <div className="flex flex-col md:flex-row items-center gap-8">
                                <div className="h-[240px] w-[240px] relative">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={data}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={60}
                                                outerRadius={80}
                                                paddingAngle={5}
                                                dataKey="value"
                                                stroke="none"
                                            >
                                                {holdings.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                contentStyle={{ backgroundColor: '#000', borderColor: '#333', borderRadius: '8px' }}
                                                itemStyle={{ color: '#fff' }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                    <div className="absolute inset-0 flex items-center justify-center text-center flex-col">
                                        <div className="text-xs text-muted-foreground">Active</div>
                                        <div className="font-bold text-white">{holdings.length} Pos</div>
                                    </div>
                                </div>

                                <div className="flex-1 w-full space-y-3">
                                    {holdings.map((h) => (
                                        <div key={`${h.name}-${h.symbol}`} className="flex items-center justify-between text-sm">
                                            <div className="flex items-center gap-2 truncate max-w-[150px]">
                                                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: h.color }} />
                                                <span className="text-gray-300 truncate">{h.name}</span>
                                            </div>
                                            <span className="font-mono text-gray-500">{((h.value / (totalEquity - balance || 1)) * 100).toFixed(0)}%</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-12 text-muted-foreground">
                                No active positions. Start predicting to build your portfolio.
                            </div>
                        )}
                    </div>
                </div>

                <div className="grid lg:grid-cols-1 gap-8 mt-8">
                    {/* Your Assets List */}
                    <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-6">
                        <h3 className="text-xl font-bold text-white mb-6">Your Assets</h3>
                        <div className="space-y-4">
                            {holdings.length > 0 ? holdings.map((asset, idx) => (
                                <div
                                    key={`${asset.name}-${idx}`}
                                    className="flex items-center justify-between p-4 bg-black/40 rounded-xl border border-white/5 hover:border-white/20 transition-all cursor-pointer group"
                                    onClick={() => navigate(`/markets/${asset.marketSlug}`)}
                                >
                                    <div className="flex items-center gap-4 overflow-hidden">
                                        <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-black flex-shrink-0" style={{ backgroundColor: asset.color }}>
                                            {asset.symbol[0]}
                                        </div>
                                        <div className="overflow-hidden">
                                            <div className="font-bold text-white text-lg truncate flex items-center gap-2">
                                                {asset.name}
                                                {asset.recommendation && (
                                                    <span className={`flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border ${asset.recommendation.type === 'SUCCESS' ? 'text-emerald-400 border-emerald-400/30 bg-emerald-400/10' :
                                                        asset.recommendation.type === 'WARNING' ? 'text-yellow-400 border-yellow-400/30 bg-yellow-400/10' :
                                                            'text-red-400 border-red-400/30 bg-red-400/10'
                                                        }`} title={asset.recommendation.message}>
                                                        {asset.recommendation.type === 'SUCCESS' ? <CheckCircle size={10} /> : <AlertCircle size={10} />}
                                                        {asset.recommendation.type === 'SUCCESS' ? 'ALIGNED' : 'REVIEW'}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs text-muted-foreground font-mono">{asset.amount} Shares ({asset.symbol})</div>
                                        </div>
                                    </div>
                                    <div className="text-right flex-shrink-0 ml-2">
                                        <div className="font-bold text-white font-mono">${asset.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                        <div className={`text-xs font-bold flex items-center justify-end gap-1 ${asset.change >= 0 ? 'text-secondary' : 'text-red-500'}`}>
                                            {asset.change >= 0 ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                                            {Math.abs(asset.change).toFixed(2)}%
                                        </div>
                                    </div>
                                </div>
                            )) : (
                                <div className="text-center py-12 text-muted-foreground">
                                    Your active predictions will appear here.
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* My AI Agents Section */}
                <div className="mt-8 mb-12">
                    <MyAgents />
                </div>
            </div>
        </div >
    );
};
